import React, { FC, useCallback, useMemo } from 'react';
import Image from 'frontastic/lib/image';
import { Product, Variant } from 'shared/types/product';
import { discardRepeatedValues } from '../product-details/helpers/discardRepeatedValues';
import debounce from 'lodash.debounce';

interface ColorSwatchProps {
  variants: Variant[];
  product?: Product;
  setSelectedVariant?: any;
}

/** A tile has room for the product's own colourway plus at most two alternatives. */
const SWATCH_COUNT = 3;

/**
 * The colour swatches under a CMS product tile.
 *
 * Two behaviours worth knowing, because neither is obvious from the markup:
 *
 * 1. THE PINNED COLOURWAY LEADS. `product.selectedVariant` is the variant the tile opens on, and
 *    for a CMS slider driven by `product_ids` that is the exact SKU the entry pinned. Algolia
 *    returns variants in its own order, so without this the pinned colourway was often outside the
 *    first three and its swatch never appeared on the tile showing it.
 *
 * 2. SELECTION IS STICKY. Hovering a swatch used to swap the variant and leaving the row put it
 *    straight back to `masterVariantID` -- which `mapProduct` force-sets to the chrome variant, so
 *    the tile always snapped back to chrome no matter what the entry asked for. A shopper
 *    comparing finishes had to keep the cursor still to look at one. The variant now stays on
 *    whichever swatch was last hovered or clicked.
 *
 * The order is computed from the PINNED variant, not the live selection, so the swatches do not
 * reshuffle under the cursor as the selection changes.
 */
const ColorSwatches: FC<ColorSwatchProps> = ({ variants, setSelectedVariant, product }) => {
  const pinnedId = product?.selectedVariant?.id;

  const variantsToUse = useMemo<Variant[]>(() => {
    const usable: Variant[] =
      discardRepeatedValues(variants, 'colour')?.filter(
        (v: Variant) =>
          v?.attributes?.colour && String(v.attributes.colour).trim().toLowerCase() !== 'not applicable',
      ) ?? [];

    const pinned = usable.find((v) => v.id === pinnedId);
    return pinned ? [pinned, ...usable.filter((v) => v.id !== pinned.id)] : usable;
  }, [variants, pinnedId]);

  const showViewMore = variantsToUse.length > SWATCH_COUNT;

  // Debounced so dragging the cursor across the row does not re-render the tile once per swatch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateVariant = useCallback(
    debounce((variant: Variant) => setSelectedVariant?.(variant), 100),
    [setSelectedVariant],
  );

  return (
    <div className="flex flex-row">
      {variantsToUse.slice(0, SWATCH_COUNT).map((variant, index) => (
        <div
          key={variant.id}
          onMouseEnter={() => updateVariant(variant)}
          // The tile is wrapped in a link, so a swatch click would otherwise open the PDP.
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            updateVariant(variant);
          }}
          className={`relative lg:w-20 lg:w-20 xl:w-20 xl:w-20 2xl:w-20 2xl:w-20 md:h-20 md:w-20 w-15 h-15 rounded-full ${
            index === SWATCH_COUNT - 1 && showViewMore ? 'border border-[#333] bg-white' : ''
          } mr-2`}
        >
          <Image
            src={`/images/colorswatches/swatch_${variant?.attributes?.colour
              .replaceAll(' ', '')
              .replaceAll('/', '')}.png`}
            alt={variant?.attributes?.colour ?? ''}
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            priority
          />
        </div>
      ))}
      {showViewMore && (
        <div
          key="viewMore"
          className="relative mr-2 flex items-center justify-center text-8 md:text-10 lg:text-10 xl:text-10 2xl:text-10 text-[#333] 2xl:h-24 w-15  2xl:w-24"
        >
          <span className="mt-1 ml-4 text-8 hover:underline">View More</span>
        </div>
      )}
    </div>
  );
};

export default ColorSwatches;
